import React, { useState, useEffect } from 'react';
import { Button, Form, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import KaModal from '../Common/KaModal';
import { OrderDisplayDto } from '../../requests/useSearchOrders';
import { KaSelectOption, toKaSelectOptions, KaSelect } from '../Common/KaSelect';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';
import SpinnerButton from '../Common/SpinnerButton';
import useAddOrderTransaction from '../../requests/dev/useAddOrderTransaction';
import { toAbbreviation, unitList } from '../Common/Units';

interface CreateTransactionModalProps {
  order: OrderDisplayDto;
  onHide: () => void;
  onSuccess: () => void;
}

interface BlendItemWithAmount {
  blendItemId: string;
  name: string;
  amount: number;
  unit: string;
  dispensedAmount: number;
  dispensedUnit: string;
}

const CreateTransactionModal = ({ order, onHide, onSuccess }: CreateTransactionModalProps) => {
  const [selectedSite, setSelectedSite] = useState<KaSelectOption | null>(null);
  const [transactionNumber, setTransactionNumber] = useState('');
  const [blendItems, setBlendItems] = useState<BlendItemWithAmount[]>([]);

  const getSites = useGetSitesNonAdmin();

  const createTransaction = useAddOrderTransaction({
    onSuccess: () => onSuccess(),
    onError: (error) => alert(`Failed to create transaction: ${error.message}`),
  });

  const siteOptions = toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true });

  useEffect(() => {
    if (order && getSites.data) {
      if (!selectedSite && order.siteNames?.length > 0) {
        const firstSiteName = order.siteNames[0];
        const matchingSite = getSites.data.find(site => site.name === firstSiteName);
        if (matchingSite) {
          const defaultSiteOption = siteOptions.find(s => s.value === matchingSite.id);
          if (defaultSiteOption) {
            setSelectedSite(defaultSiteOption);
          }
        }
      }
    }
  }, [order, getSites.data, selectedSite, siteOptions]);

  useEffect(() => {
    const defaultItems: BlendItemWithAmount[] = order?.blends.flatMap(blend =>
      blend.items.map(item => ({
        blendItemId: item.ref,
        name: item.name,
        amount: item.amount,
        unit: item.unit,
        dispensedAmount: item.amount,
        dispensedUnit: item.unit
      }))
    ) ?? [];

    setBlendItems(defaultItems);
  }, [order, setBlendItems]);

  const handleClose = () => {
    setSelectedSite(null);
    setTransactionNumber('');
    setBlendItems([]);
    onHide();
  };

  const handleAmountChange = (itemRef: string, amount: string) => {
    const numAmount = parseFloat(amount) || 0;
    setBlendItems(prev => prev.map(item =>
      item.blendItemId === itemRef
        ? { ...item, dispensedAmount: numAmount }
        : item
    ));
  };

  const handleUnitChange = (itemRef: string, unit: string) => {
    console.log(unit);
    setBlendItems(prev => prev.map(item =>
      item.blendItemId === itemRef
        ? { ...item, dispensedUnit: unit }
        : item
    ));
  };

  const handleSubmit = () => {
    if (!selectedSite) return;

    createTransaction.request({
      orderId: order.id,
      siteId: selectedSite.value,
      transactionNumber,
      blendItemAmounts: blendItems.map(item => ({
        blendItemId: item.blendItemId,
        amount: item.dispensedAmount
      }))
    });
  };

  const isValid = selectedSite && transactionNumber.length > 0;

  const blendGroups = order.blends.map((blend, blendIndex) => ({
    index: blendIndex,
    items: blend.items.map(item => {
      const itemWithAmount = blendItems.find(bi => bi.blendItemId === item.ref);
      return {
        ...item,
        dispensedAmount: itemWithAmount?.dispensedAmount || item.amount,
        dispensedUnit: itemWithAmount?.dispensedUnit || item.unit
      };
    })
  }));

  return (
    <KaModal
      show={true}
      onHide={handleClose}
      title="Create Order Transaction"
      size="lg"
      body={
        <Form>
          <FormGroup>
            <Label for="transactionNumber">Transaction Number *</Label>
            <Input
              type="text"
              id="transactionNumber"
              value={transactionNumber}
              onChange={(e) => setTransactionNumber(e.target.value)}
              placeholder="Enter transaction number..."
            />
          </FormGroup>

          <FormGroup>
            <Label for="site">Site *</Label>
            <KaSelect
              inputId="site"
              isClearable
              options={siteOptions}
              value={selectedSite}
              onChange={(newValue) => setSelectedSite(newValue as KaSelectOption | null)}
              placeholder="Select a site..."
            />
          </FormGroup>

          <h6 className="mt-6 mb-3">Dispensed Amounts</h6>
          {blendGroups.map((blend) => (
            <div key={blend.index} className="mb-4">
              <h6 className="text-primary mb-2">Blend {blend.index + 1}</h6>
              <div className="ps-3">
                {blend.items.map((item, itemIndex) => (
                  <Row key={item.ref} className="mb-2 align-items-center">
                    <Col xs={5}>
                      <Label className="mb-0 fw-normal">
                        {item.name}
                      </Label>
                      <div className="text-muted small">
                        Requested: {item.amount.toFixed(2)} {item.unit}
                      </div>
                    </Col>
                    <Col xs={4}>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.dispensedAmount.toString()}
                        onChange={(e) => handleAmountChange(item.ref, e.target.value)}
                        placeholder="Dispensed amount"
                        bsSize="sm"
                      />
                    </Col>
                    <Col xs={3}>
                      <Input
                        type="select"
                        value={item.dispensedUnit}
                        onChange={(e) => handleUnitChange(item.ref, e.target.value)}
                        bsSize="sm"
                      >
                        {unitList().map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {toAbbreviation(unit.value)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                ))}
              </div>
            </div>
          ))}
        </Form>
      }
      footer={
        <>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <SpinnerButton
            spinning={createTransaction.isLoading}
            onClick={handleSubmit}
            disabled={!isValid}>
            Create Transaction
          </SpinnerButton>
        </>
      }
    />
  );
};

export default CreateTransactionModal;
